import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  MapPin, Phone, Camera, CheckCircle, Clock, User, Building2, 
  Navigation, Upload, X, AlertTriangle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function FieldVerificationPage() {
  const { checkId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checkData, setCheckData] = useState(null);
  const [error, setError] = useState(null);
  
  // Photo capture
  const [photos, setPhotos] = useState([]);
  const [capturing, setCapturing] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  // GPS
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  
  // Form
  const [remarks, setRemarks] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('');

  useEffect(() => {
    fetchCheckData();
    getLocation();
    
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [checkId, token]);

  const fetchCheckData = async () => {
    try {
      const response = await axios.get(`/field-verification/${checkId}?token=${token}`);
      setCheckData(response.data);
    } catch (err) {
      setError('Invalid or expired verification link');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (err) => {
        setLocationError('Unable to retrieve your location. Please enable GPS.');
        console.error(err);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' }, 
        audio: false 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCapturing(true);
    } catch (err) {
      toast.error('Unable to access camera');
      console.error(err);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    // Add timestamp and GPS overlay
    const timestamp = new Date().toLocaleString('en-IN', { 
      day: '2-digit', month: 'short', year: 'numeric', 
      hour: '2-digit', minute: '2-digit', second: '2-digit' 
    });
    
    context.fillStyle = 'rgba(0, 0, 0, 0.7)';
    context.fillRect(0, canvas.height - 60, canvas.width, 60);
    
    context.fillStyle = 'white';
    context.font = '14px Arial';
    context.fillText(`Date: ${timestamp}`, 10, canvas.height - 40);
    
    if (location) {
      context.fillText(`GPS: ${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`, 10, canvas.height - 20);
    } else {
      context.fillText('GPS: Not available', 10, canvas.height - 20);
    }

    const photoData = canvas.toDataURL('image/jpeg', 0.8);
    setPhotos([...photos, {
      id: Date.now(),
      data: photoData,
      timestamp,
      location: location ? { ...location } : null
    }]);

    toast.success('Photo captured with GPS tag');
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setCapturing(false);
  };

  const removePhoto = (photoId) => {
    setPhotos(photos.filter(p => p.id !== photoId));
  };

  const handleSubmit = async () => {
    if (photos.length === 0) {
      toast.error('Please capture at least one photo');
      return;
    }
    if (!verificationStatus) {
      toast.error('Please select verification status');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`/field-verification/${checkId}/submit`, {
        token,
        photos: photos.map(p => ({
          data: p.data,
          timestamp: p.timestamp,
          latitude: p.location?.latitude,
          longitude: p.location?.longitude
        })),
        location,
        remarks,
        verification_status: verificationStatus,
        submitted_at: new Date().toISOString()
      });
      
      toast.success('Verification submitted successfully!');
      setCheckData({ ...checkData, status: 'submitted' });
    } catch (err) {
      toast.error('Failed to submit verification');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-slate-600">Loading verification details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Error</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (checkData?.status === 'submitted' || checkData?.status === 'completed') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Verification Submitted</h2>
            <p className="text-slate-600">This verification has already been completed.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 p-4">
      <div className="max-w-lg mx-auto space-y-4">
        {/* Header */}
        <Card className="bg-blue-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-6 h-6" />
              <h1 className="text-xl font-bold">Field Verification</h1>
            </div>
            <p className="text-blue-100 text-sm">Check ID: {checkId}</p>
          </CardContent>
        </Card>

        {/* Candidate Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <User className="w-4 h-4" />
              Candidate Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <p className="text-xs text-slate-500">Name</p>
              <p className="font-medium">{checkData?.employee_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Check Type</p>
              <Badge variant="outline">{checkData?.check_type?.replace(/_/g, ' ') || '-'}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Address to Visit */}
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-800">
              <MapPin className="w-4 h-4" />
              Address to Visit
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-amber-900 font-medium mb-3">
              {checkData?.verification_address || checkData?.address || 'Address not provided'}
            </p>
            {checkData?.verification_address && (
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(checkData.verification_address)}`, '_blank')}
              >
                <Navigation className="w-4 h-4 mr-2" />
                Open in Maps
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Contact Numbers */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Contact Numbers
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {checkData?.contact_number && (
              <a 
                href={`tel:${checkData.contact_number}`} 
                className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg"
              >
                <div>
                  <p className="text-xs text-slate-500">Primary Contact</p>
                  <p className="font-medium text-emerald-700">{checkData.contact_number}</p>
                </div>
                <Phone className="w-5 h-5 text-emerald-600" />
              </a>
            )}
            {checkData?.alternate_contact && (
              <a 
                href={`tel:${checkData.alternate_contact}`} 
                className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg"
              >
                <div>
                  <p className="text-xs text-slate-500">Alternate Contact</p>
                  <p className="font-medium text-blue-700">{checkData.alternate_contact}</p>
                </div>
                <Phone className="w-5 h-5 text-blue-600" />
              </a>
            )}
            {!checkData?.contact_number && !checkData?.alternate_contact && (
              <p className="text-slate-400 text-center py-2">No contact numbers available</p>
            )}
          </CardContent>
        </Card>

        {/* GPS Status */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className={`w-5 h-5 ${location ? 'text-emerald-500' : 'text-rose-500'}`} />
                <span className="text-sm font-medium">GPS Status</span>
              </div>
              {location ? (
                <Badge className="bg-emerald-100 text-emerald-700">
                  Located ({location.accuracy.toFixed(0)}m accuracy)
                </Badge>
              ) : (
                <div className="flex items-center gap-2">
                  <Badge className="bg-rose-100 text-rose-700">Not Available</Badge>
                  <Button variant="outline" size="sm" onClick={getLocation}>Retry</Button>
                </div>
              )}
            </div>
            {locationError && (
              <p className="text-xs text-rose-500 mt-2">{locationError}</p>
            )}
          </CardContent>
        </Card>

        {/* Photo Capture */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Camera className="w-4 h-4" />
              Photo Capture (GPS Tagged)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {capturing ? (
              <div className="space-y-3">
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline 
                  className="w-full rounded-lg bg-black"
                />
                <canvas ref={canvasRef} className="hidden" />
                <div className="flex gap-2">
                  <Button onClick={capturePhoto} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                    <Camera className="w-4 h-4 mr-2" />
                    Capture
                  </Button>
                  <Button onClick={stopCamera} variant="outline">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <Button onClick={startCamera} className="w-full" variant="outline">
                <Camera className="w-4 h-4 mr-2" />
                Open Camera
              </Button>
            )}

            {/* Captured Photos */}
            {photos.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium">Captured Photos ({photos.length})</p>
                <div className="grid grid-cols-2 gap-2">
                  {photos.map(photo => (
                    <div key={photo.id} className="relative">
                      <img src={photo.data} alt="Captured" className="w-full h-24 object-cover rounded-lg" />
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        className="absolute top-1 right-1 h-6 w-6 p-0"
                        onClick={() => removePhoto(photo.id)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Verification Status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Verification Status *</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {['Address Verified', 'Address Not Found', 'Partially Verified', 'Unable to Verify'].map(status => (
              <Button
                key={status}
                variant={verificationStatus === status ? 'default' : 'outline'}
                className={`w-full justify-start ${verificationStatus === status ? 'bg-blue-600' : ''}`}
                onClick={() => setVerificationStatus(status)}
              >
                {verificationStatus === status && <CheckCircle className="w-4 h-4 mr-2" />}
                {status}
              </Button>
            ))}
          </CardContent>
        </Card>

        {/* Remarks */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Remarks / Observations</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea 
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter any observations or remarks..."
              rows={3}
            />
          </CardContent>
        </Card>

        {/* Submit */}
        <Button 
          onClick={handleSubmit} 
          disabled={submitting || photos.length === 0 || !verificationStatus}
          className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 mr-2" />
              Submit Verification
            </>
          )}
        </Button>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 pb-4">
          Photos will be tagged with current date, time, and GPS coordinates
        </p>
      </div>
    </div>
  );
}
